import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";

const EMPTY = { name: "", rating: 5, comment: "", service: "", source: "", date: "", visible: true };

export default function AdminReviews() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/reviews").then((r) => setItems(r.data)).catch(() => toast.error("Chargement impossible"));
  useEffect(() => { load(); }, []);

  const openForm = (item = null) => {
    setEditing(item);
    setForm(item ? { name: item.name, rating: item.rating, comment: item.comment || "", service: item.service || "", source: item.source || "", date: item.date || "", visible: item.visible } : EMPTY);
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/reviews/${editing.id}`, form);
      else await api.post("/admin/reviews", form);
      toast.success("Avis enregistré");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (item) => {
    try {
      await api.put(`/admin/reviews/${item.id}`, { name: item.name, rating: item.rating, comment: item.comment || "", service: item.service || "", source: item.source || "", date: item.date || "", visible: !item.visible });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (item) => {
    if (!window.confirm("Supprimer cet avis ?")) return;
    await api.delete(`/admin/reviews/${item.id}`);
    toast.success("Avis supprimé");
    load();
  };

  const move = async (index, dir) => {
    const arr = [...items];
    const j = index + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[index], arr[j]] = [arr[j], arr[index]];
    setItems(arr);
    try {
      await api.put("/admin/reviews-order", { ids: arr.map((x) => x.id) });
    } catch {
      load();
    }
  };

  const inputCls = "w-full rounded-xl border border-line bg-cream px-4 py-2.5 text-sm text-ink placeholder:text-muted2 focus:outline-none focus:ring-2 focus:ring-forest/30 focus:border-forest";

  return (
    <div data-testid="admin-avis">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display font-semibold text-2xl sm:text-3xl text-ink">Avis clients</h1>
          <p className="mt-1 text-sm text-muted2">Les avis visibles alimentent automatiquement la section « Ils nous ont fait confiance ».</p>
        </div>
        <button onClick={() => openForm()} data-testid="add-review-btn" className="inline-flex items-center gap-2 rounded-full bg-forest text-cream text-sm font-semibold px-5 py-2.5 hover:bg-pine transition-colors">
          <Plus className="h-4 w-4" /> Ajouter un avis
        </button>
      </div>

      {!items ? (
        <p className="mt-10 text-sm text-muted2">Chargement…</p>
      ) : items.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-line bg-white p-12 text-center text-sm text-muted2" data-testid="avis-empty">
          Aucun avis. Ajoutez un vrai témoignage client.
        </div>
      ) : (
        <div className="mt-8 space-y-3">
          {items.map((r, i) => (
            <article key={r.id} data-testid={`review-item-${r.id}`} className="rounded-2xl bg-white border border-line p-5 flex flex-wrap items-center gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex gap-0.5">
                    {Array.from({ length: 5 }).map((_, s) => (
                      <Star key={s} className={`h-3.5 w-3.5 ${s < r.rating ? "fill-terra text-terra" : "text-line"}`} />
                    ))}
                  </div>
                  <h3 className="font-semibold text-sm text-ink">{r.name}</h3>
                  {r.is_placeholder && <span className="rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold px-2 py-0.5">Exemple</span>}
                </div>
                <p className="mt-1.5 text-sm text-body2 line-clamp-2">{r.comment}</p>
                {r.service && <p className="mt-1 text-xs text-muted2">{r.service}{r.source ? ` — ${r.source}` : ""}</p>}
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-xs text-body2">
                  <Switch checked={r.visible} onCheckedChange={() => toggle(r)} data-testid={`review-visible-${r.id}`} />
                  {r.visible ? "Visible" : "Masqué"}
                </label>
                <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Monter" className="p-1.5 rounded-lg hover:bg-sand disabled:opacity-30"><ArrowUp className="h-4 w-4 text-body2" /></button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label="Descendre" className="p-1.5 rounded-lg hover:bg-sand disabled:opacity-30"><ArrowDown className="h-4 w-4 text-body2" /></button>
                <button onClick={() => openForm(r)} aria-label="Modifier" className="p-1.5 rounded-lg hover:bg-sand" data-testid={`review-edit-${r.id}`}><Pencil className="h-4 w-4 text-body2" /></button>
                <button onClick={() => remove(r)} aria-label="Supprimer" className="p-1.5 rounded-lg hover:bg-red-50" data-testid={`review-delete-${r.id}`}><Trash2 className="h-4 w-4 text-red-600" /></button>
              </div>
            </article>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="review-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Modifier l'avis" : "Nouvel avis"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Prénom / nom affiché" data-testid="review-form-name" className={inputCls} />
            <div className="grid grid-cols-2 gap-3">
              <select value={form.rating} onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })} data-testid="review-form-rating" className={inputCls}>
                {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} étoile{n > 1 ? "s" : ""}</option>)}
              </select>
              <input value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} placeholder="Prestation" data-testid="review-form-service" className={inputCls} />
            </div>
            <textarea required value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="Commentaire" rows={4} data-testid="review-form-comment" className={`${inputCls} resize-none`} />
            <div className="grid grid-cols-2 gap-3">
              <input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="Source (ex. Google)" data-testid="review-form-source" className={inputCls} />
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="review-form-date" className={inputCls} />
            </div>
            <label className="flex items-center gap-3 text-sm text-body2">
              <Switch checked={form.visible} onCheckedChange={(v) => setForm({ ...form, visible: v })} data-testid="review-form-visible" />
              Visible sur le site
            </label>
            <button type="submit" disabled={saving} data-testid="review-form-submit" className="w-full rounded-full bg-forest text-cream font-semibold py-3 text-sm hover:bg-pine transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Enregistrer
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
